import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Mail, MapPin, Building2, Award } from 'lucide-react';
import SEOHead from './SEOHead';
import Footer from './Footer';

const ContactUs = () => {

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 py-8">
      <SEOHead
        title="Contact Us"
        description="Get in touch with the Nerd Nation team. We're here to help students with their academic journey, platform questions and feedback."
        path="/contact"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-800 text-white">
            <CardTitle className="flex items-center gap-3 text-2xl sm:text-3xl">
              <Mail className="h-7 w-7 sm:h-8 sm:w-8" />
              Contact Us
            </CardTitle>
            <p className="text-orange-100 text-sm mt-2">We're here to help you succeed!</p>
          </CardHeader>
          
          <CardContent className="p-6 sm:p-8 space-y-8">
            
            {/* Incubation Highlight */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-6 shadow-md">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <Award className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-amber-900 mb-2">
                    Proudly Incubated at
                  </h3>
                  <p className="text-lg sm:text-xl font-semibold text-amber-800">
                    DEIS - JNTU University Hyderabad
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    Fostering innovation and excellence in education technology
                  </p>
                </div>
              </div>
            </div>

            {/* Registered Address */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Registered Address</h3>
                  <div className="space-y-1 text-gray-700">
                    <p className="font-medium">GHMC NO. 8-2-626/5, 2ND FLR</p>
                    <p className="font-medium">Reliance Classic, FN 201</p>
                    <p>Banjara Hills, Hyderabad</p>
                    <p>Khairatabad, Telangana, India</p>
                    <p className="font-semibold text-blue-600">PIN: 500034</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Corporate Office */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Building2 className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Corporate Office</h3>
                  <div className="space-y-1 text-gray-700">
                    <p className="font-medium">Modern Profound Tech Park</p>
                    <p>Hitech City, Hyderabad</p>
                    <p className="font-semibold text-green-600">PIN: 500081</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Contact */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl p-6 shadow-md">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Get in Touch</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Have questions? We're here to help! Reach out to us via email.
                  </p>
                  <a 
                    href="mailto:support@nerdindians.com"
                    className="inline-flex items-center gap-2 text-lg sm:text-xl font-bold text-orange-600 hover:text-orange-700 hover:underline transition-colors"
                  >
                    <span>📧</span>
                    support@nerdindians.com
                  </a>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Business Hours</h3>
              <div className="space-y-2 text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Monday - Friday:</span>
                  <span>9:00 AM - 6:00 PM IST</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Saturday:</span>
                  <span>10:00 AM - 4:00 PM IST</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Sunday:</span>
                  <span className="text-red-600">Closed</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Email support available 24/7. We typically respond within 24-48 hours.
              </p>
            </div>

            {/* Footer */}
            <div className="border-t pt-6 text-center">
              <p className="text-gray-600">
                <span className="font-semibold text-orange-600">NERD AI</span> - India's First AI That Prepares You to Win
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Empowering students and professionals with AI-powered learning solutions
              </p>
            </div>

          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ContactUs;
